import { usePlatformStore } from '../stores/platformStore';
import { resolveAssistantPrefix } from '../utils/harmonyParser';

import type {
  // Request types
  FileAddRequest,
  FileUpdateByIDRequest,
  CollectionCreateRequest,
  CollectionUpdateRequest,
  AddFileToCollectionRequest,
  MagExportRequest,
  STTRequest,
  OptionsRequest,
  // Response types
  FileInfo,
  FileListResponse,
  FileByIDResponse,
  FileUpdateByIDResponse,
  FileDeleteByIDResponse,
  HistoryModel,
  HistoryResponse,
  HistoryByIDResponse,
  ChatResponse,
  CollectionModel,
  CollectionListResponse,
  CollectionShare,
  CollectionShareListResponse,
  CollectionSharePermission,
  CollectionShareResponse,
  CollectionUnshareResponse,
  ShareableUser,
  ShareableUserListResponse,
  CollectionCreateResponse,
  CollectionUpdateResponse,
  CollectionDeleteResponse,
  ModelInfo,
  ModelListResponse,
  ModelByIDResponse,
  AdminUser,
  AdminUserCreateRequest,
  AdminUserUpdateRequest,
  AdminUserListResponse,
  AdminAPIKey,
  AdminAPIKeyCreateRequest,
  AdminAPIKeyCreateResponse,
  AdminAPIKeyListResponse,
  STTResponse,
  TTSResponse,
  SearchableDocument,
  OptionsResponse,
  MessageModel,
  ModelImportAvailableResponse,
  ModelImportResponse,
} from '../types/models';

export class DuplicateFileError extends Error {
  constructor(message = 'File already exists') {
    super(message);
    this.name = 'DuplicateFileError';
  }
}

/**
 * Gets the base URL for API requests
 * Desktop mode: uses localhost (backend runs locally)
 * Web mode: uses current origin (backend runs on same server or configured remote)
 * Uses platform store to determine the correct base URL
 */
const getBaseURL = (): string => {
  // Get baseURL from platform store (handles isDesktop check internally)
  // Always use absolute URLs - no webpack dev server proxy needed
  const baseURL = usePlatformStore.getState().baseURL();

  // Debug logging in development
  if (process.env.NODE_ENV === 'development') {
    const isDesktop = usePlatformStore.getState().isDesktop();
    console.log('[APIClient] getBaseURL:', { baseURL, isDesktop });
  }

  return baseURL;
};

/**
 * Generic API request handler
 * Handles HTTP requests with proper error handling
 */
const apiRequest = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const baseURL = getBaseURL();
  const url = `${baseURL}${endpoint}`;

  // Debug logging in development
  if (process.env.NODE_ENV === 'development') {
    console.log('[APIClient] Making request:', { url, baseURL, endpoint });
  }

  try {
    const response = await fetch(url, {
      ...options,
      credentials: 'include', // Include cookies for JWT authentication
      headers: {
        // Only set Content-Type for non-FormData requests
        ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    // Handle empty responses (e.g., 204 No Content)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return undefined as T;
    }

    return response.json();
  } catch (error) {
    // Handle network errors (offline, DNS failure, CORS, etc.)
    if (error instanceof TypeError) {
      // Log the actual error for debugging
      console.error('API request failed:', {
        url,
        baseURL,
        error: error.message,
        stack: error.stack,
      });
      throw new Error(`Network error: Unable to connect to server at ${url}. Please check your internet connection and ensure the backend is running.`);
    }
    // Re-throw other errors (like HTTP errors we already formatted)
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`API request to ${endpoint} failed: ${String(error)}`);
  }
};

/**
 * Streaming API request handler for chat completions and other streaming endpoints
 * Returns Response object for manual stream handling
 */
const apiRequestStreamResponse = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  const baseURL = getBaseURL();
  const url = `${baseURL}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
      'Cache-Control': 'no-cache',
      'Connection': 'close', // Force new TCP connection per request
      ...options.headers,
    },
    cache: 'no-store',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response;
};

/**
 * Process SSE stream for chat completions with chunk callbacks
 * Dispatches custom events for each chunk and handles completion
 */
export const processChatCompletionStream = async (
  response: Response,
  onChunk?: (data: {
    content?: string;
    done?: boolean;
    toolCalls?: any[];
    citations?: any[];
  }) => void
): Promise<void> => {
  if (!response.body) {
    throw new Error('Response body is null');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let hasReceivedContent = false;
  let prefixBuffer = '';

  if (process.env.NODE_ENV === 'development') {
    console.log('[Chat Stream] Starting to process response stream');
  }

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[Chat Stream] Reader done, remaining buffer:', buffer.length > 0 ? buffer.substring(0, 100) : '(empty)');
        }
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.trim() || line.startsWith(':')) continue;
        if (!line.startsWith('data: ')) continue;

        const data = line.slice(6); // Remove 'data: ' prefix

        if (data === '[DONE]') {
          // Dispatch final done event
          const doneData = { done: true, toolCalls: [], citations: [] };
          if (onChunk) {
            onChunk(doneData);
          }
          window.dispatchEvent(new CustomEvent('chat-completion-stream-chunk', {
            detail: doneData
          }));
          await reader.cancel('Stream completed');
          return;
        }

        try {
          const parsed = JSON.parse(data);

          // .delta for streaming
          const rawContent =
            parsed.content ||
            parsed.delta?.content ||
            parsed.choices?.[0]?.delta?.content ||
            parsed.choices?.[0]?.message?.content ||
            '';
          let content = rawContent;
          if (!hasReceivedContent && rawContent) {
            prefixBuffer += rawContent;
            const resolved = resolveAssistantPrefix(prefixBuffer, { allowPending: true });
            if (resolved.pending) {
              content = '';
            } else {
              content = resolved.content;
              prefixBuffer = '';
            }
          }
          if (content.trim().length > 0) {
            hasReceivedContent = true;
          }

          const chunkData = {
            content,
            done: parsed.done || false,
            toolCalls: parsed.tool_calls || parsed.toolCalls || parsed.choices?.[0]?.delta?.tool_calls || [],
            citations:
              parsed.choices?.[0]?.citations ||
              [],
          };

          // Debug logging in development
          // if (process.env.NODE_ENV === 'development' && content) {
          //   console.log('[Chat Stream] Chunk received:', { contentLength: content.length });
          // }

          // Call callback if provided
          if (onChunk) {
            onChunk(chunkData);
          }

          // Dispatch event for other listeners (like Chat.tsx)
          window.dispatchEvent(new CustomEvent('chat-completion-stream-chunk', {
            detail: chunkData
          }));
        } catch (e) {
          console.warn('[Chat Stream] Failed to parse SSE chunk:', data, e);
        }
      }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    await reader.cancel(errorMessage);
    throw error;
  } finally {
    reader.releaseLock();
  }
};


/**
 * Process SSE stream for file uploads with progress events
 *
 * requestId is echoed so consumers can filter during concurrent uploads.
 */

export interface ProcessFileUploadStreamOptions {
  requestId?: string;
}

const processFileUploadStream = async (
  response: Response,
  options?: ProcessFileUploadStreamOptions,
): Promise<FileInfo> => {
  if (!response.body) {
    throw new Error('Response body is null');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalResult: FileInfo | null = null;
  let chunkCount = 0;

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.trim() || line.startsWith(':')) continue;
        if (!line.startsWith('data: ')) continue;

        const data = line.slice(6); // Remove 'data: ' prefix

        if (data === '[DONE]') {
          await reader.cancel('Upload completed');
          if (!finalResult) {
            if (chunkCount === 0) {
              throw new DuplicateFileError();
            }
            throw new Error('File upload completed but no result was received');
          }
          return finalResult;
        }

        let parsed: Record<string, any>;
        try {
          parsed = JSON.parse(data);
        } catch {
          throw new Error('Invalid upload response. The document was not confirmed as saved.');
        }
        chunkCount++;
        if (parsed.status === 'error') {
          throw new Error('Document could not be safely processed. Check the file format and privacy models.');
        }
        const detail = options?.requestId !== undefined
          ? { ...parsed, requestId: options.requestId } : parsed;
        window.dispatchEvent(new CustomEvent('file-upload-progress', { detail }));
        if (parsed.id && parsed.progress === 1 && parsed.privacy_status === 'complete'
            && typeof parsed.original_retained === 'boolean') {
          finalResult = parsed as FileInfo;
        }
      }
    }

    if (!finalResult) {
      throw new Error('File upload stream ended without result');
    }
    throw new Error('File upload stream ended before completion was confirmed.');
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    await reader.cancel(errorMessage);
    throw error;
  } finally {
    reader.releaseLock();
  }
};

/**
 * Unified API Client
 * Provides a consistent interface for all backend API operations
 *
 * All methods use HTTP fetch, making them compatible with both
 * desktop (Electron) and web (browser) environments
 */
export const apiClient = {
  feedback: {
    create: async (data: {
      kind: 'bug' | 'idea' | 'usability' | 'other';
      message: string;
      is_blocking: boolean;
      allow_follow_up: boolean;
      surface?: string;
      route?: string;
      build_version?: string;
      trace_id?: string;
      context?: Record<string, unknown>;
    }) => apiRequest<{ id: string; status: 'received' }>('/feedback', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  },

  /**
   * File operations
   */
  files: {
    listFiles: async (): Promise<FileInfo[]> => {
      const response = await apiRequest<FileListResponse>('/files');
      return response.data;
    },

    getFileById: async (fileId: string): Promise<FileInfo> =>
      apiRequest<FileByIDResponse>(`/files/${fileId}`),

    addFile: async (
      fileAddRequest: FileAddRequest,
      options?: { requestId?: string },
    ): Promise<FileInfo> => {
      const response = await fetch(`${getBaseURL()}/files`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...fileAddRequest, privacy_policy: 'phi_strict', original_retention: 'discard' }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Request failed' }));
        throw new Error(error.detail || `HTTP ${response.status}`);
      }

      // Process SSE stream to get final result and progress updates
      return processFileUploadStream(response, options);
    },

    uploadMagazine: async (
      file: File,
      params?: { collection_id?: string; migrate?: boolean },
      options?: { requestId?: string },
    ): Promise<FileInfo> => {
      const fd = new FormData();
      fd.append('file', file, file.name);
      fd.append('title', file.name);
      fd.append('content_type', '.mag');
      fd.append('collection_id', params?.collection_id ?? '-1');
      if (params?.migrate) fd.append('migrate', 'true');

      const response = await fetch(`${getBaseURL()}/files`, {
        method: 'POST',
        credentials: 'include',
        // Do NOT set Content-Type ; the browser adds the multipart boundary.
        headers: { Accept: 'text/event-stream' },
        body: fd,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Request failed' }));
        throw new Error(error.detail || `HTTP ${response.status}`);
      }

      return processFileUploadStream(response, options);
    },

    updateFileById: async (
      fileId: string,
      updateRequest: FileUpdateByIDRequest
    ): Promise<FileInfo> => {
      await apiRequest<FileUpdateByIDResponse>(`/files/${fileId}`, {
        method: 'POST',
        body: JSON.stringify(updateRequest),
      });
      // Re-fetch the updated file info
      return apiClient.files.getFileById(fileId);
    },

    deleteFileById: async (fileId: string): Promise<{ success: boolean; id: string }> => {
      const response = await apiRequest<FileDeleteByIDResponse>(`/files/${fileId}`, {
        method: 'DELETE',
      });
      return { success: response.deleted, id: response.id };
    },

    stopFileUpload: async (): Promise<{ success: boolean }> => {
      await apiRequest('/files/stop', { method: 'POST' });
      return { success: true };
    },

    exportMagazine: async (
      params: MagExportRequest,
    ): Promise<{ b64_mag: string } | Blob> => {
      const response = await fetch(`${getBaseURL()}/files/export`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Request failed' }));
        throw new Error(error.detail || `HTTP ${response.status}`);
      }

      if (params.stream) {
        return response.blob();
      }
      // LEGACY base64 path : delete this branch once all callers set stream:true.
      return response.json();
    },

    getFileContentUrl: (fileId: string): string =>
      `${getBaseURL()}/files/${fileId}/content`,
  },

  /**
   * Chat history operations
   */
  history: {
    listHistory: async (): Promise<HistoryModel[]> => {
      const response = await apiRequest<HistoryResponse>('/history');
      return response.data;
    },

    getHistoryById: async (historyId: string): Promise<MessageModel[]> => {
      const response = await apiRequest<HistoryByIDResponse>(`/history/${historyId}`);
      return response.messages;
    },

    updateHistoryById: async (
      historyId: string,
      messages: MessageModel[],
      title?: string
    ): Promise<{ id: string; title: string; messages: MessageModel[] }> =>
      apiRequest(`/history/${historyId}`, {
        method: 'POST',
        body: JSON.stringify({ chat_id: historyId, messages, title }),
      }),

    deleteHistoryById: async (historyId: string): Promise<{ success: boolean }> =>
      apiRequest<{ success: boolean }>(`/history/${historyId}`, {
        method: 'DELETE',
      }),
  },

  /**
   * Chat completion operations
   */
  chat: {
    chatCompletion: async (messages: MessageModel[], model: string) =>
      apiRequest<ChatResponse>('/chat/completions', {
        method: 'POST',
        body: JSON.stringify({ messages, model }),
      }),

    chatCompletionStream: async (
      messages: MessageModel[],
      model: string,
      documents: string[] = [],
      stream: boolean = true
    ): Promise<Response> => {
      return apiRequestStreamResponse('/chat/completions', {
        method: 'POST',
        body: JSON.stringify({ messages, model, documents, stream }),
      });
    },

    stopGeneration: async (): Promise<{ success: boolean }> =>
      apiRequest<{ success: boolean }>('/chat/completions/stop', { method: 'POST' }),
  },

  /**
   * Collection operations
   */
  collections: {
    getCollections: async (): Promise<CollectionModel[]> => {
      const response = await apiRequest<CollectionListResponse>('/collections');
      return response.data;
    },

    createCollection: async (request: CollectionCreateRequest): Promise<CollectionModel> =>
      apiRequest<CollectionCreateResponse>('/collections', {
        method: 'POST',
        body: JSON.stringify(request),
      }) as Promise<CollectionModel>,

    getCollectionById: async (collectionId: string): Promise<CollectionModel> =>
      apiRequest<CollectionModel>(`/collections/${collectionId}`),

    updateCollectionById: async (
      collectionId: string,
      updateRequest: CollectionUpdateRequest
    ): Promise<CollectionModel> => {
      await apiRequest<CollectionUpdateResponse>(`/collections/${collectionId}`, {
        method: 'POST',
        body: JSON.stringify(updateRequest),
      });
      // Re-fetch the updated collection
      return apiClient.collections.getCollectionById(collectionId);
    },

    deleteCollectionById: async (collectionId: string): Promise<{ success: boolean }> => {
      const response = await apiRequest<CollectionDeleteResponse>(`/collections/${collectionId}`, {
        method: 'DELETE',
      });
      return { success: response.deleted };
    },

    getFilesInCollection: async (collectionId: string): Promise<FileInfo[]> => {
      const response = await apiRequest<FileListResponse>(`/collections/${collectionId}/files`);
      return response.data;
    },

    addFileToCollection: async (
      collectionId: string,
      fileRequest: AddFileToCollectionRequest
    ): Promise<FileInfo> => {
      const response = await fetch(`${getBaseURL()}/collections/${collectionId}/files`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...fileRequest, privacy_policy: 'phi_strict' }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Request failed' }));
        throw new Error(error.detail || `HTTP ${response.status}`);
      }

      // Process SSE stream to get final result and progress updates
      return processFileUploadStream(response);
    },

    removeFileFromCollection: async (
      collectionId: string,
      fileId: string
    ): Promise<{ success: boolean; id: string }> => {
      const response = await apiRequest<FileDeleteByIDResponse>(`/collections/${collectionId}/files/${fileId}`, {
        method: 'DELETE',
      });
      return { success: response.deleted, id: response.id };
    },

    assignFileToCollection: async (
      collectionId: string,
      fileId: string
    ): Promise<{ assigned: boolean; id: string }> => {
      const response = await apiRequest<{ id: string; collection_id: string; assigned: boolean }>(
        `/collections/${collectionId}/files/${fileId}`,
        { method: 'PUT' }
      );
      return { assigned: response.assigned, id: response.id };
    },

    /**
     * Sharing — grants named users access to a collection, and with it the
     * documents filed inside. Only the collection's owner may grant or revoke.
     */
    getCollectionShares: async (collectionId: string): Promise<CollectionShare[]> => {
      const response = await apiRequest<CollectionShareListResponse>(
        `/collections/${collectionId}/shares`
      );
      return response.data;
    },

    shareCollection: async (
      collectionId: string,
      userId: string,
      permission: CollectionSharePermission = 'view'
    ): Promise<CollectionShareResponse> =>
      apiRequest<CollectionShareResponse>(`/collections/${collectionId}/shares`, {
        method: 'POST',
        body: JSON.stringify({ user_id: userId, permission }),
      }),

    unshareCollection: async (
      collectionId: string,
      userId: string
    ): Promise<{ success: boolean }> => {
      const response = await apiRequest<CollectionUnshareResponse>(
        `/collections/${collectionId}/shares/${userId}`,
        { method: 'DELETE' }
      );
      return { success: response.unshared };
    },

    getShareableUsers: async (): Promise<ShareableUser[]> => {
      const response = await apiRequest<ShareableUserListResponse>(
        '/collections/shareable-users'
      );
      return response.data;
    },
  },

  /**
   * Model operations
   */
  models: {
    getModels: async (): Promise<ModelInfo[]> => {
      const response = await apiRequest<ModelListResponse>('/models');
      return response.data;
    },

    getModelsInfo: async (): Promise<ModelListResponse> =>
      apiRequest<ModelListResponse>('/models'),

    getModelsInfoById: async (model: string): Promise<ModelByIDResponse> =>
      apiRequest<ModelByIDResponse>(`/models/${model}`),

    deleteModelById: async (
      model: string,
      deleteFiles: boolean = true
    ): Promise<{ deleted: boolean; deleted_files: string[] }> =>
      apiRequest<{ deleted: boolean; deleted_files: string[] }>(
        `/models/${encodeURIComponent(model)}?delete_files=${deleteFiles ? 'true' : 'false'}`,
        { method: 'DELETE' }
      ),

    // List model packs staged in the appliance import directory (admin only).
    getAvailableModelPacks: async (): Promise<ModelImportAvailableResponse> =>
      apiRequest<ModelImportAvailableResponse>('/models/import/available'),

    // Import a staged model pack into the model store; refreshes the registry
    // live (no restart). Admin only.
    importModelPack: async (pack: string): Promise<ModelImportResponse> =>
      apiRequest<ModelImportResponse>('/models/import', {
        method: 'POST',
        body: JSON.stringify({ pack }),
      }),

    // Upload an .er model pack from the browser and install it live. Uses XHR so
    // we get upload progress (fetch can't report it). Admin only.
    uploadModelPack: (
      file: File,
      onProgress?: (percent: number) => void
    ): Promise<ModelImportResponse> =>
      new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${getBaseURL()}/models/import/upload`);
        xhr.withCredentials = true; // send the JWT cookie
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable && onProgress) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText) as ModelImportResponse);
            } catch {
              reject(new Error('Malformed server response.'));
            }
          } else {
            let msg = `HTTP ${xhr.status}`;
            try {
              msg = JSON.parse(xhr.responseText).detail || msg;
            } catch {
              /* keep status */
            }
            reject(new Error(msg));
          }
        };
        xhr.onerror = () => reject(new Error('Upload failed (network error).'));
        const form = new FormData();
        form.append('file', file);
        xhr.send(form);
      }),
  },

  /**
   * Admin operations (Web mode, superuser only)
   */
  admin: {
    listUsers: async (): Promise<AdminUser[]> => {
      const response = await apiRequest<AdminUserListResponse>('/admin/users');
      return response.data;
    },

    createUser: async (request: AdminUserCreateRequest): Promise<AdminUser> =>
      apiRequest<AdminUser>('/admin/users', {
        method: 'POST',
        body: JSON.stringify(request),
      }),

    updateUser: async (
      userId: string,
      request: AdminUserUpdateRequest
    ): Promise<AdminUser> =>
      apiRequest<AdminUser>(`/admin/users/${encodeURIComponent(userId)}`, {
        method: 'POST',
        body: JSON.stringify(request),
      }),

    resetPassword: async (userId: string, password: string): Promise<AdminUser> =>
      apiRequest<AdminUser>(`/admin/users/${encodeURIComponent(userId)}/password`, {
        method: 'POST',
        body: JSON.stringify({ password }),
      }),

    deleteUser: async (
      userId: string
    ): Promise<{ deleted: boolean; purged_resources: any }> =>
      apiRequest<{ deleted: boolean; purged_resources: any }>(
        `/admin/users/${encodeURIComponent(userId)}`,
        { method: 'DELETE' }
      ),

    listAPIKeys: async (): Promise<AdminAPIKey[]> => {
      const response = await apiRequest<AdminAPIKeyListResponse>('/admin/api-keys');
      return response.data;
    },

    createAPIKey: async (
      request: AdminAPIKeyCreateRequest
    ): Promise<AdminAPIKeyCreateResponse> =>
      apiRequest<AdminAPIKeyCreateResponse>('/admin/api-keys', {
        method: 'POST',
        body: JSON.stringify(request),
      }),

    revokeAPIKey: async (keyId: string): Promise<{ id: string; revoked: boolean }> =>
      apiRequest<{ id: string; revoked: boolean }>(
        `/admin/api-keys/${encodeURIComponent(keyId)}`,
        { method: 'DELETE' }
      ),
  },

  /**
   * Audio operations (STT/TTS)
   */
  audio: {
    transcribeAudio: async (audioRequest: STTRequest): Promise<STTResponse> =>
      apiRequest<STTResponse>('/audio/transcriptions', {
        method: 'POST',
        body: JSON.stringify(audioRequest),
      }),

    textToSpeech: async (
      message: MessageModel,
      language?: string,
      sanitize: boolean = true,
    ): Promise<TTSResponse> =>
      apiRequest<TTSResponse>('/audio/speech', {
        method: 'POST',
        body: JSON.stringify({
          input: message.content,
          language,
          sanitize,
        }),
      }),

    textToSpeechStream: async (
      input: string,
      voice: string = 'af_sarah',
      language: string = 'en-us',
      speed: number = 1.0
    ): Promise<Response> => {
      return apiRequestStreamResponse('/audio/speech/stream', {
        method: 'POST',
        body: JSON.stringify({ input, voice, language, speed }),
      });
    },

    stopTTSStream: async (): Promise<{ success: boolean }> =>
      apiRequest<{ success: boolean }>('/audio/speech/stream/stop', { method: 'POST' }),

  },

  /**
   * Search operations
   */
  search: {
    query: async (query: string): Promise<SearchableDocument[]> => {
      const response = await apiRequest<{ chats: HistoryModel[]; files: FileInfo[] }>(
        `/search?query=${encodeURIComponent(query)}`
      );
      // console.log('[APIClient] Search response:', response);
      const chatDocs: SearchableDocument[] = (response.chats || []).map((chat) => ({
        id: `chat_${chat.id}`,
        sourceType: 'chat' as const,
        title: chat.title || 'Untitled Chat',
        lastModified: chat.last_updated,
      }));
      // console.log('[APIClient] Mapped chat documents:', chatDocs);
      const fileDocs: SearchableDocument[] = (response.files || []).map((file) => ({
        id: `file_${file.id}`,
        sourceType: 'file' as const,
        title: file.filename,
        lastModified: file.created_at,
      }));
      // console.log('[APIClient] Mapped file documents:', fileDocs);
      return [...chatDocs, ...fileDocs];
    }
  },

  /**
   * Backend options/configuration
   */
  options: {
    getBackendOptions: async (): Promise<OptionsResponse> =>
      apiRequest<OptionsResponse>('/options'),

    setBackendOptions: async (options: OptionsRequest): Promise<OptionsResponse> =>
      apiRequest<OptionsResponse>('/options', {
        method: 'POST',
        body: JSON.stringify(options),
      }),

    resetBackendOption: async (resetPayload: { exclude: string[] }): Promise<OptionsResponse> =>
      apiRequest<OptionsResponse>('/options/reset', {
        method: 'POST',
        body: JSON.stringify(resetPayload),
      }),
  },

  /**
   * Tools operations
   * TODO: Define proper Tool response types when backend is implemented
   */
  tools: {
    getTools: async (): Promise<any> =>
      apiRequest<any>('/tools'),

    getTool: async (toolId: string): Promise<any> =>
      apiRequest<any>(`/tools/${toolId}`),

    runTool: async (toolId: string, parameters: any): Promise<any> =>
      apiRequest<any>(`/tools/${toolId}`, {
        method: 'POST',
        body: JSON.stringify(parameters),
      }),
  },

  /**
   * Deployment mode
   */
  deployment: {
    getMode: async (): Promise<{ deployment_mode: string }> =>
      apiRequest<{ deployment_mode: string }>('/deployment'),
  },

  /**
   * Authentication operations (Web mode only)
   */
  auth: {
    login: async (email: string, password: string): Promise<void> => {
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);

      const response = await fetch(`${getBaseURL()}/auth/jwt/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Login failed' }));
        throw new Error(error.detail || 'Login failed');
      }
    },

    register: async (
      email: string,
      password: string,
      firstName?: string,
      lastName?: string
    ): Promise<void> => {
      const response = await fetch(`${getBaseURL()}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          first_name: firstName,
          last_name: lastName,
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Registration failed' }));
        throw new Error(error.detail || 'Registration failed');
      }
    },

    logout: async (): Promise<void> => {
      await fetch(`${getBaseURL()}/auth/jwt/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    },

    getCurrentUser: async (): Promise<any> => {
      const response = await fetch(`${getBaseURL()}/users/me`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Not authenticated');
      }

      return response.json();
    },
  },
};
